import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Directions } from "@mui/icons-material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Handle, Node, NodeProps, Position, useUpdateNodeInternals } from "@xyflow/react";
import { CellClassParams, ColDef, KeyCreatorParams, ValueFormatterParams } from "ag-grid-community";
import { useDebouncedCallback } from "use-debounce";

import CableRenderer, { formatSpliceRecordCable } from "fond/topologicalView/CableRenderer";
import FiberCellRenderer, { formatFiberRange } from "fond/topologicalView/FiberCellRenderer";
import HandleRenderer from "fond/topologicalView/HandleRenderer";
import LeafRenderer from "fond/topologicalView/LeafRenderer";
import SpliceRangeNodeSkeleton from "fond/topologicalView/SpliceRangeNodeSkeleton";
import { useTopologicalViewContext } from "fond/topologicalView/TopologicalViewContext";
import { SpliceRecord, SpliceRecordCable, TopologicalNodeData } from "fond/topologicalView/types";
import { AgGrid } from "fond/widgets";

const gridOptions = {
  rowGroupPanelShow: "never" as const,
  sideBar: false,
  pagination: false,
  suppressMovableColumns: true,
  domLayout: "autoHeight" as const,
  defaultColDef: {
    sortable: true,
    hide: true,
    resizable: true,
  },
  enableCellTextSelection: true,
  ensureDomOrder: true,
};

const spliceRecordCableFormatter = (params: ValueFormatterParams<SpliceRecord, SpliceRecordCable>) => {
  return formatSpliceRecordCable({ value: params.value, recordCount: params.data?.recordCount });
};

const styleCableCell = (params: CellClassParams<SpliceRecord, SpliceRecordCable>) => {
  if (params.value === "dead" || params.value === "spare") {
    return null;
  }
  const backgroundColor = params.value?.cable.backgroundColor ?? null;
  if (backgroundColor) {
    return { backgroundColor };
  } else {
    return null;
  }
};

const SpliceRangeNode: React.FC<Pick<NodeProps<Node<TopologicalNodeData>>, "data">> = ({ data }) => {
  const hasLeafRow = useMemo(() => data.spliceRecords.some((row) => row.leaf !== null), [data]);
  const { ref, inView } = useInView();
  const [visible, setVisible] = useState<boolean>(false);
  const slowlySetVisible = useDebouncedCallback((value) => setVisible(value), 15_000);
  const currentlyVisible = inView || visible;
  const { selectFeature } = useTopologicalViewContext();
  const updateNodeInternals = useUpdateNodeInternals();
  const onGridReady = useCallback(() => updateNodeInternals(data.id), [updateNodeInternals, data.id]);

  // If the node is in view, show it immediately. If it goes out of view, wait a while before hiding it again.
  useEffect(() => {
    if (inView) {
      slowlySetVisible.cancel();
      setVisible(true);
    } else {
      slowlySetVisible(false);
    }
  }, [inView, setVisible, slowlySetVisible]);

  const onShowFeatureProperties = useCallback(() => {
    selectFeature(
      { id: data.id, layerKey: data.layer.key },
      {
        highlightFeature: true,
        showProperties: true,
        zoomMap: true,
      }
    );
  }, [data.id, data.layer.key, selectFeature]);

  const colDefs: ColDef<SpliceRecord>[] = useMemo(
    () => [
      {
        field: "input.handleId",
        headerName: "",
        hide: false,
        cellRenderer: HandleRenderer,
        cellRendererParams: { type: "target" },
        width: 15,
        maxWidth: 15,
        minWidth: 15,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
      },
      {
        field: "input",
        headerName: "Cable In",
        cellRenderer: CableRenderer,
        cellRendererParams: { side: "input" },
        valueFormatter: spliceRecordCableFormatter,
        width: 250,
        minWidth: 250,
        lockVisible: true,
        hide: false,
        cellStyle: styleCableCell,
        filter: "agSetColumnFilter",
        filterParams: { valueFormatter: spliceRecordCableFormatter },
        flex: 1,
      },
      { field: "input.tier", width: 125, minWidth: 125, maxWidth: 125, filter: "agTextColumnFilter" },
      {
        field: "input.fiberRange",
        headerName: "Fiber In",
        width: 125,
        minWidth: 125,
        lockVisible: true,
        hide: false,
        cellRenderer: FiberCellRenderer,
        cellRendererParams: { side: "input" },
        valueFormatter: (params) => formatFiberRange(params.value),
      },
      {
        field: "spliceType",
        headerName: "Splice Type",
        filter: true,
      },
      {
        field: "output",
        headerName: "Cable Out",
        cellRenderer: CableRenderer,
        cellRendererParams: { side: "output" },
        valueFormatter: spliceRecordCableFormatter,
        width: 250,
        minWidth: 250,
        lockVisible: true,
        hide: false,
        cellStyle: styleCableCell,
        filter: "agSetColumnFilter",
        filterParams: { valueFormatter: spliceRecordCableFormatter },
        flex: 1,
      },
      { field: "output.tier" },
      {
        field: "output.fiberRange",
        headerName: "Fiber Out",
        width: 125,
        minWidth: 125,
        lockVisible: true,
        hide: false,
        cellRenderer: FiberCellRenderer,
        cellRendererParams: { side: "output" },
        valueFormatter: (params) => formatFiberRange(params.value),
      },
      {
        field: "leaf",
        headerName: "Service Location",
        cellRenderer: LeafRenderer,
        width: 100,
        minWidth: 100,
        // Only show the 'Service Location' column by default if there are leaf children.
        hide: !hasLeafRow,
        flex: 1,
        filter: "agSetColumnFilter",
        filterParams: {
          keyCreator: (params: KeyCreatorParams<SpliceRecord, { label: string }>) => params.value?.label,
          valueFormatter: (params: ValueFormatterParams<SpliceRecord, { label: string }>) => params.value?.label,
        },
        valueFormatter: (params) => params.value?.label,
      },
      {
        field: "output.handleId",
        headerName: "",
        cellRenderer: HandleRenderer,
        cellRendererParams: { type: "source" },
        hide: false,
        width: 15,
        maxWidth: 15,
        minWidth: 15,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
        lockVisible: true,
      },
    ],
    [hasLeafRow]
  );

  return (
    <div
      ref={ref}
      style={{
        minWidth: 950,
        maxHeight: 800,
        textAlign: "center",
        backgroundColor: "white",
        border: "1px solid #bbb",
        borderRadius: 4,
      }}
    >
      <Stack justifyContent="center" alignItems="center" direction="row" sx={{ py: 1, borderBottom: "1px solid #bbb" }}>
        <Typography>{data.label}</Typography>
        {data.id && (
          <Tooltip title="View feature" arrow>
            <IconButton size="small" color="primary" onClick={onShowFeatureProperties}>
              <Directions style={{ fontSize: "18px" }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {data.spliceRecords.length === 0 && <Handle type="source" position={Position.Right} />}
      {data.spliceRecords.length > 0 && currentlyVisible && (
        <Box
          data-testid="splice-node-grid"
          sx={{
            ".ag-theme-alpine .ag-root-wrapper": {
              border: "none !important",
            },
          }}
        >
          <AgGrid columnDefs={colDefs} rowData={data.spliceRecords} gridOptions={gridOptions} className="nodrag" onGridReady={onGridReady} />
        </Box>
      )}
      {data.spliceRecords.length > 0 && !currentlyVisible && (
        <SpliceRangeNodeSkeleton spliceRecords={data.spliceRecords} dimensions={data.dimensions} />
      )}
    </div>
  );
};

export default SpliceRangeNode;
